import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthorizeNetConfig, Payment } from '@vsolv/packages/payments/domain';
import { Observable, catchError, firstValueFrom, of } from 'rxjs';

/** One way to pay a Pay Later balance: one of its term's payment schedules. Amounts are in cents. */
export interface PayLaterPlanOption {
  scheduleId: string;
  title: string;
  installments: number;
  installmentSubtotal: number;
  installmentTax: number;
  installmentAmount: number;
  total: number;
  intervalDays: number;
}

@Injectable()
export class PaymentService {
  constructor(private http: HttpClient) {}

  list(dto: Payment.ListRequest) {
    return firstValueFrom(
      this.http.get<Payment.ListResponse>('/api/payments', {
        params: { ...dto, excludeIds: dto.excludeIds?.join(',') ?? '', status: dto.status?.join(',') ?? '' },
      })
    );
  }

  list$(dto: Payment.ListRequest): Observable<Payment.ListResponse> {
    return this.http.get<Payment.ListResponse>('/api/payments', {
      params: { ...dto, excludeIds: dto.excludeIds?.join(',') ?? '', status: dto.status?.join(',') ?? '' },
    });
  }

  customerPaymentList(dto: Payment.ListRequest) {
    return firstValueFrom(
      this.http.get<Payment.ListResponse>('/api/payments/customerPayments', {
        params: { ...dto, excludeIds: dto.excludeIds?.join(',') ?? '', status: dto.status?.join(',') ?? '' },
      })
    );
  }

  async retrieve(paymentId: string) {
    return await firstValueFrom(
      this.http.get<Payment.RetrieveResponse>(`/api/payments/${paymentId}`).pipe(
        catchError(err => {
          if (err.status === 404) return of(null);
          throw err;
        })
      )
    );
  }

  changePaymentMethod(paymentId: string, paymentMethodId: string) {
    return firstValueFrom(
      this.http.post<Payment.ChangePaymentMethodResponse>(`/api/payments/${paymentId}/payment-method`, {
        paymentMethodId,
      })
    );
  }

  removePaymentMethod(paymentId: string) {
    return firstValueFrom(
      this.http.delete<Payment.RemovePaymentMethodResponse>(`/api/payments/${paymentId}/payment-method`)
    );
  }

  retryPayment(paymentId: string, paymentMethodId?: string) {
    return firstValueFrom(
      this.http.post<Payment.RetryPaymentResponse>(`/api/payments/${paymentId}/retry`, {
        paymentMethodId,
      })
    );
  }

  settleExternalPayment(paymentId: string, receipt: Payment.SettleExternalPaymentRequest) {
    return firstValueFrom(
      this.http.post<Payment.RetryPaymentResponse>(`/api/payments/${paymentId}/settle-external`, receipt)
    );
  }

  /** The term's payment schedules offered for an outstanding Pay Later balance (VS-1458). */
  listPayLaterPlans(paymentId: string) {
    return firstValueFrom(
      this.http.get<{ options: PayLaterPlanOption[] }>(`/api/payments/${paymentId}/pay-later-plans`)
    );
  }

  /** Replaces a Pay Later balance with an installment plan and collects (or receipts) the first one. */
  payLaterInInstallments(
    paymentId: string,
    body: {
      scheduleId: string;
      paymentMethodId: string;
      firstInstallmentReceipt?: Omit<Payment.SettleExternalPaymentRequest, 'attachmentStorageItemId'>;
    }
  ) {
    return firstValueFrom(
      this.http.post<{ warrantyId: string; paymentIds: string[] }>(`/api/payments/${paymentId}/pay-later-plans`, body)
    );
  }

  uploadReceiptAttachment(paymentId: string, file: File) {
    const data = new FormData();
    data.set('file', file);
    return firstValueFrom(this.http.post<{ id: string }>(`/api/payments/${paymentId}/proof-of-receipt`, data));
  }

  receiptAttachmentUrl(paymentId: string) {
    return firstValueFrom(this.http.get<{ url: string }>(`/api/payments/${paymentId}/proof-of-receipt`));
  }

  resendPaymentReminder(paymentId: string) {
    return firstValueFrom(
      this.http.post<Payment.ResendPaymentReminderResponse>(`/api/payments/${paymentId}/resend-reminder`, {})
    );
  }

  async createAuthorizeNetConfing(body: AuthorizeNetConfig.Model) {
    const value = await firstValueFrom(
      this.http.post<AuthorizeNetConfig.CreateResponse>(`/api/authorize-net`, { ...body })
    );
    return value;
  }
  deleteAuthorizeNetConfig() {
    return firstValueFrom(this.http.delete<boolean>(`/api/authorize-net`, {}));
  }

  retrieveAuthorizeNetConfig() {
    return firstValueFrom(this.http.get<AuthorizeNetConfig.RetrieveResponse>(`/api/authorize-net`, {}));
  }
}
